import React, { useState, useEffect } from 'react';
import { Download, FileText, Archive, Search, Filter } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { mockResources, loadFromLocalStorage, saveToLocalStorage } from '../data/mockData';
import AnimatedText from '../components/AnimatedText';
import FloatingCard from '../components/FloatingCard';
import ShimmerButton from '../components/ShimmerButton';

const Resources = () => {
  const [resources, setResources] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [filteredResources, setFilteredResources] = useState([]);

  useEffect(() => {
    const savedResources = loadFromLocalStorage('soleil_resources', mockResources);
    setResources(savedResources);
    setFilteredResources(savedResources);
  }, []);

  useEffect(() => {
    let filtered = resources;

    if (searchTerm) {
      filtered = filtered.filter(resource => 
        resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        resource.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (categoryFilter !== 'all') {
      filtered = filtered.filter(resource => resource.category === categoryFilter);
    }

    setFilteredResources(filtered);
  }, [resources, searchTerm, categoryFilter]);

  const handleDownload = (resourceId) => {
    const resource = resources.find(r => r.id === resourceId);
    if (resource) {
      const updatedResources = resources.map(r => 
        r.id === resourceId 
          ? { ...r, downloadCount: r.downloadCount + 1 }
          : r
      );
      setResources(updatedResources);
      saveToLocalStorage('soleil_resources', updatedResources);
      
      alert(`Téléchargement de "${resource.title}"... Ceci est une démo, aucun fichier réel ne sera téléchargé.`);
    }
  };

  const getFileIcon = (fileType) => {
    switch (fileType) {
      case 'PDF':
        return <FileText className="w-8 h-8 text-red-400" />;
      case 'ZIP':
        return <Archive className="w-8 h-8 text-amber-400" />;
      default:
        return <FileText className="w-8 h-8 text-blue-400" />;
    }
  };

  const categories = ['all', ...new Set(resources.map(r => r.category))];

  return (
    <div className="min-h-screen bg-gray-900">
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-20">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_70%,rgba(251,191,36,0.1),transparent_70%)]"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <AnimatedText 
              className="text-4xl sm:text-5xl font-bold text-white mb-6"
              isH1={true}
            >
              On Vous <span className="title-gradient">Aide</span>
            </AnimatedText>
            <AnimatedText 
              className="text-xl text-gray-300 leading-relaxed font-inter"
              delay={200}
            >
              Téléchargez nos ressources complètes, guides et outils pour vous aider à maîtriser la communication, le marketing et le développement de marque.
            </AnimatedText>
          </div>
        </div>
      </section>

      <section className="py-12 bg-gray-900 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                placeholder="Rechercher des ressources..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 font-inter bg-gray-800 border-gray-700 text-white placeholder-gray-400"
              />
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Filter className="w-5 h-5 text-gray-400" />
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="w-40 bg-gray-800 border-gray-700 text-white">
                    <SelectValue placeholder="Catégorie" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700">
                    {categories.map(category => (
                      <SelectItem key={category} value={category} className="text-white hover:bg-gray-700">
                        {category === 'all' ? 'Toutes les Catégories' : category}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="text-sm text-gray-400 font-inter">
                {filteredResources.length} ressources trouvées
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredResources.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-24 h-24 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="w-12 h-12 text-gray-500" />
              </div>
              <h3 className="text-xl font-inter font-semibold text-white mb-2">Aucune ressource trouvée</h3>
              <p className="text-gray-400 font-inter">Essayez d&apos;ajuster vos critères de recherche ou de filtre.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredResources.map((resource, index) => (
                <AnimatedText key={resource.id} delay={200 + index * 100}>
                  <FloatingCard className="bg-gray-800 border border-gray-700 overflow-hidden hover:border-amber-500/50 transition-all duration-300" delay={index * 0.1}>
                    <div className="aspect-video bg-gradient-to-br from-gray-700 to-gray-800 flex items-center justify-center relative">
                      <div className="bg-gray-900 rounded-full p-4 shadow-lg">
                        {getFileIcon(resource.fileType)}
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                        <span className="text-white text-sm font-inter">Aperçu disponible</span>
                      </div>
                    </div>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-3">
                        <Badge variant="secondary" className="bg-amber-500/20 text-amber-400 font-inter">
                          {resource.category}
                        </Badge>
                        <span className="text-sm text-gray-400 font-inter">{resource.fileType}</span>
                      </div>
                      <h3 className="text-xl font-inter font-semibold text-white mb-3 line-clamp-2">
                        {resource.title}
                      </h3>
                      <p className="text-gray-400 text-sm leading-relaxed mb-4 line-clamp-3 font-inter">
                        {resource.description}
                      </p>
                      <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center space-x-4 text-sm text-gray-500 font-inter">
                          <span>{resource.size}</span>
                          <span>•</span>
                          <span>{resource.downloadCount} téléchargements</span>
                        </div>
                      </div>
                      <ShimmerButton 
                        onClick={() => handleDownload(resource.id)}
                        className="w-full bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white rounded-full"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Télécharger
                      </ShimmerButton>
                    </CardContent>
                  </FloatingCard>
                </AnimatedText>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-20 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-inter font-bold text-white mb-4">
              Approuvé par les Professionnels
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto font-inter">
              Nos ressources ont aidé des milliers d&apos;entreprises à améliorer leurs efforts de communication et de marketing.
            </p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { number: "10 000+", label: "Téléchargements Totaux" },
              { number: "25+", label: "Ressources Disponibles" },
              { number: "1 500+", label: "Utilisateurs Actifs" },
              { number: "98%", label: "Taux de Satisfaction" }
            ].map((stat, index) => (
              <FloatingCard key={index} className="bg-gray-900 border border-gray-700 text-center hover:border-amber-500/50 transition-all duration-300" delay={index * 0.2}>
                <CardContent className="p-8">
                  <div className="text-3xl font-inter font-bold text-amber-500 mb-2">{stat.number}</div>
                  <div className="text-gray-400 font-inter">{stat.label}</div>
                </CardContent>
              </FloatingCard>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-r from-amber-600 to-orange-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-3xl mx-auto space-y-8">
            <h2 className="text-3xl sm:text-4xl font-inter font-bold text-white">
              Besoin de Ressources Personnalisées ?
            </h2>
            <p className="text-xl text-amber-100 font-inter">
              Vous ne trouvez pas ce que vous cherchez ? Nous créons des ressources et guides personnalisés adaptés à vos besoins spécifiques.
            </p>
            <ShimmerButton size="lg" variant="secondary" className="bg-white text-amber-600 hover:bg-gray-100 px-8 py-6 text-lg rounded-full">
              Demander une Ressource Personnalisée
            </ShimmerButton>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Resources;
